import TrustLayout, { Section, P, UL } from "../components/TrustLayout";

const versionHistory = [
  { version: "1.0", date: "2 April 2026", notes: "Initial publication of the Technofocus Data Processing Addendum." },
];

const dpaSections = [
  { id: "dpa-intro", label: "Introduction" },
  { id: "dpa-definitions", label: "1. Definitions" },
  { id: "dpa-roles", label: "2. Roles of the Parties" },
  { id: "dpa-purpose", label: "3. Nature & Purpose of Processing" },
  { id: "dpa-categories-data", label: "4. Categories of Personal Data" },
  { id: "dpa-categories-subjects", label: "5. Categories of Data Subjects" },
  { id: "dpa-subprocessing", label: "6. Sub-processing" },
  { id: "dpa-transfers", label: "7. International Data Transfers" },
  { id: "dpa-security", label: "8. Security Measures" },
  { id: "dpa-rights", label: "9. Data Subject Rights" },
  { id: "dpa-breach", label: "10. Data Breach Notification" },
  { id: "dpa-retention", label: "11. Data Retention & Deletion" },
  { id: "dpa-audit", label: "12. Audit & Compliance" },
  { id: "dpa-liability", label: "13. Liability" },
  { id: "dpa-governing", label: "14. Governing Law" },
  { id: "dpa-contact", label: "15. Contact" },
];

export default function DPA() {
  return (
    <TrustLayout
      title="Data Processing Addendum"
      lastUpdated="2 April 2026"
      versionHistory={versionHistory}
      sections={dpaSections}
      testId="dpa-page"
    >
      {/* Intro */}
      <Section id="dpa-intro">
        <div className="rounded-xl border px-6 py-5 mb-4" style={{ borderColor: '#E2EBE9', backgroundColor: '#F8FFFE' }}>
          <P>
            This Data Processing Addendum (<strong>"DPA"</strong>) forms part of and supplements the Terms of Use, Master Services Agreement, or other applicable agreement (<strong>"Agreement"</strong>) between <strong>Technofocus Pte. Ltd.</strong> ("Technofocus", "Processor") and the customer organization (<strong>"Customer"</strong>, "Controller").
          </P>
          <P>
            This DPA applies to the processing of Personal Data by Technofocus on behalf of the Customer in connection with the use of Technofocus platforms, including but not limited to LevelUp, Why Microsoft – Compete Enablement for Partners, Skilling-in-a-Box, and associated services (collectively, the <strong>"Services"</strong>).
          </P>
        </div>
      </Section>

      {/* 1. Definitions */}
      <Section id="dpa-definitions" title="Definitions" numbered="1">
        <P>For the purposes of this DPA:</P>
        <div className="space-y-3">
          {[
            { term: '"Personal Data"', def: 'means any information relating to an identified or identifiable natural person.' },
            { term: '"Controller"', def: 'means the entity that determines the purposes and means of processing Personal Data.' },
            { term: '"Processor"', def: 'means the entity that processes Personal Data on behalf of the Controller.' },
            { term: '"Sub-processor"', def: 'means any third party engaged by the Processor to process Personal Data.' },
            { term: '"Applicable Data Protection Law"', def: 'includes the GDPR and other relevant data protection laws.' },
          ].map(({ term, def }) => (
            <div key={term} className="flex gap-3 text-sm">
              <span className="font-semibold text-gray-900 shrink-0" style={{ minWidth: '200px' }}>{term}</span>
              <span className="text-gray-600">{def}</span>
            </div>
          ))}
        </div>
      </Section>

      {/* 2. Roles */}
      <Section id="dpa-roles" title="Roles of the Parties" numbered="2">
        <UL items={[
          "The Customer acts as the Data Controller.",
          "Technofocus acts as the Data Processor.",
          "Technofocus will process Personal Data only on documented instructions from the Customer, unless required otherwise by law.",
        ]} />
      </Section>

      {/* 3. Purpose */}
      <Section id="dpa-purpose" title="Nature and Purpose of Processing" numbered="3">
        <P>Technofocus processes Personal Data for the following purposes:</P>
        <UL items={[
          "Delivery of learning and enablement programs",
          "Course enrollment and access management",
          "Tracking learner progress and completion",
          "Role-based access control for secure content portals",
          "Reporting and analytics for program sponsors",
          "Event participation and engagement tracking",
        ]} />
      </Section>

      {/* 4. Categories of Data */}
      <Section id="dpa-categories-data" title="Categories of Personal Data" numbered="4">
        <P>Technofocus processes limited Personal Data, including:</P>
        <UL items={[
          "Name",
          "Email address",
          "Organization name",
          "Job role (where applicable)",
          "Course enrollment and completion data",
          "Platform usage and interaction data",
          "Role-based access metadata",
        ]} />
        <div className="mt-4 rounded-xl border px-5 py-4 flex items-start gap-3" style={{ borderColor: '#D4EC8E', backgroundColor: '#F9FFED' }}>
          <span className="text-sm font-semibold" style={{ color: '#07641D' }}>Note:</span>
          <p className="text-sm text-gray-700">No special categories of personal data are intentionally collected or processed.</p>
        </div>
      </Section>

      {/* 5. Data Subjects */}
      <Section id="dpa-categories-subjects" title="Categories of Data Subjects" numbered="5">
        <UL items={[
          "Employees and representatives of the Customer",
          "Learners enrolled in enablement programs",
          "Event participants",
        ]} />
      </Section>

      {/* 6. Sub-processing */}
      <Section id="dpa-subprocessing" title="Sub-processing" numbered="6">
        <P>Technofocus engages Sub-processors to support the delivery of Services. A current list of Sub-processors is maintained at:</P>
        <div className="my-4">
          <a href="/subprocessors" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white transition-colors" style={{ backgroundColor: '#194449' }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#0B2E38'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = '#194449'}>
            View Sub-Processor List
          </a>
        </div>
        <P>Technofocus ensures that:</P>
        <UL items={[
          "Sub-processors are bound by contractual data protection obligations.",
          "Sub-processors provide appropriate security and confidentiality safeguards.",
          "Technofocus remains responsible for the performance of its Sub-processors.",
        ]} />
      </Section>

      {/* 7. Transfers */}
      <Section id="dpa-transfers" title="International Data Transfers" numbered="7">
        <P>Technofocus operates a globally distributed infrastructure. Personal Data may be transferred to and processed in jurisdictions outside the EU/EEA, including but not limited to: United States, Singapore, India, and United Kingdom.</P>
        <P>Such transfers are governed by appropriate safeguards, including:</P>
        <UL items={[
          "Standard Contractual Clauses (SCCs)",
          "Contractual data protection obligations with Sub-processors",
          "Industry-standard security controls",
        ]} />
      </Section>

      {/* 8. Security */}
      <Section id="dpa-security" title="Security Measures" numbered="8">
        <P>Technofocus implements appropriate technical and organizational measures, including:</P>
        <UL items={[
          "Encryption in transit (TLS)",
          "Encryption at rest (where supported by infrastructure providers)",
          "Role-based access control (RBAC)",
          "Least-privilege administrative access",
          "Logging and monitoring of system activity",
          "Secure cloud infrastructure (Microsoft Azure and equivalent providers)",
        ]} />
      </Section>

      {/* 9. Rights */}
      <Section id="dpa-rights" title="Data Subject Rights" numbered="9">
        <P>Technofocus will assist the Customer, to the extent reasonably possible, in fulfilling obligations to respond to Data Subject requests, including:</P>
        <UL items={["Access", "Rectification", "Erasure", "Restriction", "Data portability"]} />
      </Section>

      {/* 10. Breach */}
      <Section id="dpa-breach" title="Data Breach Notification" numbered="10">
        <P>Technofocus will notify the Customer without undue delay upon becoming aware of a Personal Data breach affecting Customer data and will provide relevant information to support regulatory obligations.</P>
      </Section>

      {/* 11. Retention */}
      <Section id="dpa-retention" title="Data Retention and Deletion" numbered="11">
        <P>Technofocus retains Personal Data only for as long as necessary to deliver the Services, fulfill contractual obligations, and comply with applicable laws.</P>
        <P>Upon termination of Services or upon Customer request, Personal Data will be deleted or anonymized in accordance with Technofocus' data retention policies.</P>
      </Section>

      {/* 12. Audit */}
      <Section id="dpa-audit" title="Audit and Compliance" numbered="12">
        <P>Technofocus will make available reasonable information necessary to demonstrate compliance with this DPA. Where appropriate, audits may be conducted subject to reasonable notice, confidentiality obligations, and minimization of operational disruption.</P>
      </Section>

      {/* 13. Liability */}
      <Section id="dpa-liability" title="Liability" numbered="13">
        <P>Each party's liability under this DPA is subject to the limitations of liability set out in the Agreement.</P>
      </Section>

      {/* 14. Governing Law */}
      <Section id="dpa-governing" title="Governing Law" numbered="14">
        <P>This DPA shall be governed by the laws of Singapore, unless otherwise agreed in writing.</P>
      </Section>

      {/* 15. Contact */}
      <Section id="dpa-contact" title="Contact" numbered="15">
        <P>For data protection inquiries, please contact:</P>
        <div className="rounded-xl border p-5 inline-block mt-1" style={{ borderColor: '#E2EBE9', backgroundColor: '#F8FFFE' }}>
          <p className="text-sm font-semibold text-gray-900 mb-1">Technofocus Data Protection</p>
          <a href="mailto:privacy@technofocus.ai" className="text-sm font-medium hover:underline" style={{ color: '#194449' }}>
            privacy@technofocus.ai
          </a>
        </div>
      </Section>

    </TrustLayout>
  );
}
